import { createRoot } from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import { Provider } from "react-redux";
import configureStore from "./store";
import { setStoreInstance } from "./store/instance";
import App from "./App";
// import * as serviceWorker from "./serviceWorker";
import { BrowserRouter, useHistory } from "react-router-dom";
import {
  registerStandaloneNavigate,
  setupStandaloneLinkHandling,
} from "./standaloneLinks";
import * as Sentry from "@sentry/react";
import config from "./config";
//import { IonApp } from '@ionic/react';
import { registerSW } from "virtual:pwa-register";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { I18nProvider } from "@lingui/react";
import { i18n } from "@lingui/core";
import { defaultLocale, dynamicActivate, locales } from "./i18n";
import {
  detect,
  fromStorage,
  fromNavigator,
  fromPath,
} from "@lingui/detect-locale";
import { useEffect } from "react"; // Import useEffect

const intervalMS = 60 * 60 * 1000; // 1 hour
const queryClient = new QueryClient();
registerSW({
  immediate: true,
  onRegistered(r) {
    if (r)
      setInterval(() => {
        r.update();
      }, intervalMS);
  },
});

if (config.app.host !== "localhost")
  Sentry.init({
    dsn: "https://bde1935b42df4485a1b66a353f8b7393@o1164148.ingest.sentry.io/4504162975940608",
    integrations: [],
    environment: config.app.host === "thipibo.com" ? "production" : "testing",
  });

// A lazy route chunk can 404 after a deploy, because each container image only
// carries its own build. Recover via the inline handler in index.html instead
// of dead-ending on the route. The error is still reported either way.
window.addEventListener("vite:preloadError", (event) => {
  const recovering = !!(
    window.__thipiboRecoverChunks && window.__thipiboRecoverChunks()
  );
  Sentry.captureException(event.payload || new Error("vite preload error"), {
    extra: { recovering },
  });
  // Only swallow the error when a reload is actually on its way.
  if (recovering) event.preventDefault();
});

setupStandaloneLinkHandling();

const store = configureStore();
setStoreInstance(store);
export { store };

const rootElement = document.getElementById("root");
const hideLoader = () => {};

const StandaloneLinkNavigator = () => {
  const history = useHistory();
  useEffect(() => {
    registerStandaloneNavigate((path) => history.push(path));
  }, [history]);
  return null;
};

const AppWithIntl = () => {
  useEffect(() => {
    const language = detect(
      fromPath(0),
      fromStorage("lang"),
      fromNavigator(),
      defaultLocale
    );
    dynamicActivate(language.split("-")[0]);
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <StandaloneLinkNavigator />
          <I18nProvider i18n={i18n}>
            <App onMount={hideLoader} />
          </I18nProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
};

// React 18 uses createRoot instead of render
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<AppWithIntl />);
}
